import React from 'react';
import { CheckCircle, ArrowRight, Zap, Shield, Clock, MapPin } from 'lucide-react';
import Navbar from './Navbar';

const cssStyles = `
  @keyframes pulseRing {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.15); }
  }
  @keyframes floatUp {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  @keyframes slideLeft {
    from { opacity: 0; transform: translateX(-40px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideRight {
    from { opacity: 0; transform: translateX(40px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
  @keyframes glowPulse {
    0%, 100% { box-shadow: 0 0 24px rgba(230,57,70,0.25), 0 0 0 0 rgba(230,57,70,0); }
    50% { box-shadow: 0 0 40px rgba(230,57,70,0.45), 0 0 0 6px rgba(230,57,70,0.05); }
  }
  @keyframes progressSlide {
    from { width: 0%; }
    to { width: 65%; }
  }
  .bescom-slide-left { animation: slideLeft 0.9s cubic-bezier(0.16,1,0.3,1) forwards; }
  .bescom-slide-right { animation: slideRight 0.9s cubic-bezier(0.16,1,0.3,1) 0.15s forwards; opacity: 0; }
  .bescom-float { animation: floatUp 4s ease-in-out infinite; }
  .bescom-badge-glow { animation: glowPulse 2.5s ease-in-out infinite; }
  .stat-item { animation: fadeUp 0.6s ease forwards; opacity: 0; }
  .stat-item:nth-child(1) { animation-delay: 0.6s; }
  .stat-item:nth-child(2) { animation-delay: 0.75s; }
  .stat-item:nth-child(3) { animation-delay: 0.9s; }
  .feature-item { transition: transform 0.2s ease, background 0.2s ease; cursor: default; }
  .feature-item:hover { transform: translateX(5px); background: rgba(230,57,70,0.06) !important; border-color: rgba(230,57,70,0.2) !important; }
  .cta-btn { transition: transform 0.25s ease, box-shadow 0.25s ease; }
  .cta-btn:hover { transform: translateY(-3px); box-shadow: 0 20px 50px rgba(46,204,113,0.45) !important; }
  .cta-btn:active { transform: translateY(0); }
  .progress-bar { animation: progressSlide 1.4s cubic-bezier(0.16,1,0.3,1) 1s forwards; width: 0%; }
  @media (max-width: 900px) {
    .bescom-grid { grid-template-columns: 1fr !important; }
    .bescom-image-col { display: none; }
    .bescom-left { text-align: center; }
    .bescom-left .tag-badge { justify-content: center; }
    .bescom-left .cta-row { justify-content: center; }
    .bescom-left .stats-row { justify-content: center; }
  }
`;

const BescomTransfer = () => {
  const features = [
    { icon: Zap, label: 'Fast Processing', sub: '3–5 business days' },
    { icon: Shield, label: 'Documentation Support', sub: 'End-to-end guidance' },
    { icon: MapPin, label: 'Bangalore-wide Service', sub: 'All BESCOM zones' },
    { icon: Clock, label: 'Transparent Charges', sub: 'Zero hidden fees' },
  ];

  const stats = [
    { value: '2,000+', label: 'Happy customers' },
    { value: '5 days', label: 'Avg. turnaround' },
    { value: '100%', label: 'Success rate' },
  ];

  return (
    <>
      <style>{cssStyles}</style>

      <Navbar />
      <div style={{
        background: '#080B10',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem 2rem',
        position: 'relative',
        overflow: 'hidden',
        marginTop: '-5rem',
      }}>

        {/* ── Grid texture ── */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }} />

        {/* ── Ambient red orb ── */}
        <div style={{
          position: 'absolute', top: '-80px', right: '5%', zIndex: 0,
          width: '520px', height: '520px',
          background: 'radial-gradient(circle, rgba(230,57,70,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-80px', left: '5%', zIndex: 0,
          width: '360px', height: '360px',
          background: 'radial-gradient(circle, rgba(46,204,113,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* ── Main card ── */}
        <div style={{
          maxWidth: '1120px', width: '100%',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '28px',
          padding: '3.5rem',
          position: 'relative', zIndex: 1,
          backdropFilter: 'blur(4px)',
        }}>
          <div className="bescom-grid" style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '4rem',
            alignItems: 'center',
          }}>

            {/* ─── LEFT ─── */}
            <div className="bescom-slide-left bescom-left">

              {/* Tag badge */}
              <div className="tag-badge bescom-badge-glow" style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'rgba(230,57,70,0.08)',
                border: '1px solid rgba(230,57,70,0.28)',
                borderRadius: '100px',
                padding: '6px 18px',
                marginBottom: '28px',
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: '#E63946', display: 'inline-block',
                  animation: 'pulseRing 1.6s ease-in-out infinite',
                }} />
                <span style={{
                  color: '#E63946', fontSize: '12px', fontWeight: 700,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                }}>
                  Utility Services · Bangalore
                </span>
              </div>

              {/* Headline */}
              <h1 style={{
                fontSize: 'clamp(2.2rem, 3.8vw, 3.4rem)',
                fontWeight: 900, lineHeight: 1.08,
                color: '#ffffff', marginBottom: '1.4rem',
                letterSpacing: '-0.02em',
              }}>
                BESCOM<br />
                <span style={{ color: '#E63946' }}>Name Transfer</span>
                <br />
                <span style={{ color: 'rgba(255,255,255,0.85)' }}>— Bangalore</span>
              </h1>

              <p style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '1rem', lineHeight: 1.75,
                marginBottom: '2rem', maxWidth: '470px',
              }}>
                Transfer your electricity connection name quickly and hassle-free with HousyHunt.
                We handle BESCOM documentation, application filing, and process guidance for all
                properties across Bangalore.
              </p>

              {/* Feature grid */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: '10px', marginBottom: '2.5rem',
              }}>
                {features.map(({ icon: Icon, label, sub }, i) => (
                  <div key={i} className="feature-item" style={{
                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                    padding: '14px 14px',
                    borderRadius: '14px',
                    border: '1px solid rgba(255,255,255,0.06)',
                    background: 'rgba(255,255,255,0.01)',
                  }}>
                    <div style={{
                      flexShrink: 0, padding: '8px',
                      background: 'rgba(230,57,70,0.1)',
                      borderRadius: '10px',
                    }}>
                      <Icon size={16} color="#E63946" />
                    </div>
                    <div>
                      <div style={{ color: '#ffffff', fontSize: '13px', fontWeight: 600, marginBottom: '3px' }}>
                        {label}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11.5px' }}>
                        {sub}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA row */}
              <div className="cta-row" style={{
                display: 'flex', alignItems: 'center',
                gap: '20px', flexWrap: 'wrap',
              }}>
                <button className="cta-btn" style={{
                  display: 'inline-flex', alignItems: 'center', gap: '10px',
                  background: '#2ECC71',
                  color: '#fff',
                  border: 'none', borderRadius: '100px',
                  padding: '14px 30px',
                  fontSize: '15px', fontWeight: 700,
                  cursor: 'pointer', letterSpacing: '0.01em',
                  boxShadow: '0 8px 32px rgba(46,204,113,0.3)',
                }}>
                  Apply Now: Fill the Form
                  <ArrowRight size={18} />
                </button>
                <span style={{
                  color: 'rgba(255,255,255,0.25)', fontSize: '11.5px',
                  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em',
                }}>
                  Simple. Reliable. Managed by HousyHunt.
                </span>
              </div>

              {/* Stats */}
              <div className="stats-row" style={{
                display: 'flex', gap: '36px',
                marginTop: '2.5rem', paddingTop: '2rem',
                borderTop: '1px solid rgba(255,255,255,0.07)',
              }}>
                {stats.map((s, i) => (
                  <div key={i} className="stat-item">
                    <div style={{ color: '#ffffff', fontSize: '1.55rem', fontWeight: 800, lineHeight: 1 }}>
                      {s.value}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px', marginTop: '5px' }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ─── RIGHT ─── */}
            <div className="bescom-slide-right bescom-image-col" style={{ position: 'relative' }}>

              {/* Exclusive badge */}
              <div style={{
                position: 'absolute', top: '28px', left: 0, zIndex: 10,
                background: '#E63946',
                color: '#fff', fontWeight: 700, fontSize: '11px',
                padding: '8px 20px 8px 16px',
                borderRadius: '0 10px 10px 0',
                letterSpacing: '0.09em', textTransform: 'uppercase',
                boxShadow: '0 6px 24px rgba(230,57,70,0.55)',
              }}>
                ⚡ Bangalore Exclusive
              </div>

              {/* Image frame */}
              <div className="bescom-float" style={{
                borderRadius: '22px', overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.09)',
                position: 'relative',
                boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
              }}>
                <img
                  src="https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=800&auto=format&fit=crop&q=80"
                  alt="BESCOM electricity service professional"
                  style={{
                    width: '100%', height: '460px',
                    objectFit: 'cover', display: 'block',
                  }}
                  onError={(e) => {
                    e.target.src =
                      'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&auto=format&fit=crop&q=80';
                  }}
                />
                {/* Bottom gradient */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%',
                  background: 'linear-gradient(to top, rgba(8,11,16,0.85) 0%, transparent 100%)',
                  pointerEvents: 'none',
                }} />
                {/* Subtle red vignette */}
                <div style={{
                  position: 'absolute', inset: 0,
                  boxShadow: 'inset 0 0 60px rgba(230,57,70,0.08)',
                  pointerEvents: 'none', borderRadius: '22px',
                }} />
              </div>

              {/* Floating glass card – progress */}
              <div style={{
                position: 'absolute', bottom: '36px', right: '-12px',
                background: 'rgba(14,18,24,0.92)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                padding: '16px 20px',
                minWidth: '200px',
                boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <CheckCircle size={15} color="#2ECC71" />
                  <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: 700 }}>
                    Transfer In Progress
                  </span>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginBottom: '10px' }}>
                  Name change underway…
                </div>
                <div style={{
                  height: '4px',
                  background: 'rgba(255,255,255,0.08)',
                  borderRadius: '2px', overflow: 'hidden',
                }}>
                  <div className="progress-bar" style={{
                    height: '100%',
                    background: 'linear-gradient(90deg, #2ECC71, #27AE60)',
                    borderRadius: '2px',
                    boxShadow: '0 0 8px rgba(46,204,113,0.7)',
                  }} />
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  marginTop: '6px',
                }}>
                  <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '10px' }}>Applied</span>
                  <span style={{ color: '#2ECC71', fontSize: '10px', fontWeight: 700 }}>65%</span>
                </div>
              </div>

              {/* Floating glass card – zone */}
              <div style={{
                position: 'absolute', top: '80px', right: '-12px',
                background: 'rgba(14,18,24,0.92)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '14px',
                padding: '12px 16px',
                boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '10px',
                    background: 'rgba(230,57,70,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <MapPin size={16} color="#E63946" />
                  </div>
                  <div>
                    <div style={{ color: '#ffffff', fontSize: '12px', fontWeight: 700 }}>East Zone</div>
                    <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10.5px' }}>BESCOM Indiranagar</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default BescomTransfer;